import   React         ,
       { useCallback   ,
         useEffect     ,
         useMemo       ,
         useRef        ,
         useState      }  from 'react';

import   classNames       from 'classnames';

import { Check         ,
         X             ,
         Undo2         ,
         Volume2       ,
         VolumeX       ,
         ShieldCheck   ,
         AlertTriangle ,
         FlaskConical  }  from 'lucide-react';

import   styles           from './ChatModeration.module.scss';

import { useTypedSelector } from '../../../store/useTypeSelector';
import { RootState        } from '../../../store/generalReducer';

import { ChatDecision           ,
         CHAT_MODERATION_LEVELS } from '../../../dataTypes/moderationManagment/chat_types';

import { useChatModeration } from './useChatModeration';
import { channelColor      ,
         playChime         }  from './chatModerationUi';

import   ChannelRail          from './ChannelRail';
import   RiskSpectrum         from './RiskSpectrum';
import   PendingCard          from './PendingCard';
import   DecisionFeed         from './DecisionFeed';
import   BansDrawer           from './BansDrawer';

/**
 * Onglet « Chat en direct » de l'écran Modération.
 *
 * L'IA de Google publie et refuse seule ; ne remontent ici que les messages
 * douteux (ou tous, en modération manuelle), de toutes les chaînes cochées à
 * la fois. Trois zones :
 *   - à gauche, les chaînes suivies et leurs réglages ;
 *   - au centre, le spectre de risque puis la file à valider ;
 *   - à droite, le fil de ce que les spectateurs voient, pour rattraper l'IA.
 *
 * Pensé pour le clavier : J/K (ou ↑/↓) pour passer d'un message à l'autre,
 * V publie, R refuse, E exclut, B bannit, X coche. Le focus avance tout seul.
 */

type SortMode = 'oldest' | 'risk';

interface Toast { text: string; undo?: () => void; }

const SOUND_KEY = 'kleon.chatModeration.sound';

const readSound = (): boolean => {
  try { return window.localStorage.getItem(SOUND_KEY) !== 'off'; } catch { return true; }
};

interface Props {
  /** Pour le badge de l'onglet Modération dans le menu de la régie. */
  onPendingCount? : (count: number) => void;
}

const ChatModerationBoard: React.FC<Props> = ({ onPendingCount }) => {

  const { authentication } = useTypedSelector((state: RootState) => state.authenticationReducer);
  const adminId = authentication.admin?.id ?? null;

  const state = useChatModeration(adminId);
  const { channels, channelsLoaded, selectedIds, pending, recent, error, isDemo } = state;

  const [ now          , setNow          ] = useState(Date.now());
  const [ sortMode     , setSortMode     ] = useState<SortMode>('oldest');
  const [ focusedId    , setFocusedId    ] = useState<number | null>(null);
  const [ checked      , setChecked      ] = useState<Set<number>>(new Set());
  const [ confirmBanId , setConfirmBanId ] = useState<number | null>(null);
  const [ toast        , setToast        ] = useState<Toast | null>(null);
  const [ sound        , setSound        ] = useState(readSound);
  const [ bansOpen     , setBansOpen     ] = useState(false);
  const [ freshIds     , setFreshIds     ] = useState<Set<number>>(new Set());

  const boardRef  = useRef<HTMLDivElement>(null);
  const cardRefs  = useRef<Map<number, HTMLDivElement>>(new Map());
  const seenIds   = useRef<Set<number> | null>(null);
  const toastTime = useRef<number>();

  // Les « attend depuis 40 s » avancent d'eux-mêmes.
  useEffect(() => {
    const timer = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(timer);
  }, []);

  // ── Chaînes : couleurs stables (ordre des ids), noms ───────────────────────
  const orderedIds = useMemo(() => channels.map(c => c.id).sort((a, b) => a - b), [channels]);
  const colorOf    = useCallback((id: number) => channelColor(id, orderedIds), [orderedIds]);
  const byId       = useMemo(() => new Map(channels.map(c => [c.id, c])), [channels]);
  const nameOf     = useCallback((id: number) => byId.get(id)?.name || `Chaîne ${id}`, [byId]);
  const followed   = useMemo(() => channels.filter(c => selectedIds.includes(c.id)), [channels, selectedIds]);

  // ── File triée ────────────────────────────────────────────────────────────
  const queue = useMemo(() => {
    const list = [...pending];
    if (sortMode === 'risk') list.sort((a, b) => (b.aiScore ?? 0.5) - (a.aiScore ?? 0.5) || a.id - b.id);
    else                     list.sort((a, b) => a.id - b.id);
    return list;
  }, [pending, sortMode]);

  const queueIds = queue.map(m => m.id).join(',');

  // Le focus reste sur un message de la file ; s'il vient d'en sortir, il passe
  // au suivant (même place), sinon au premier.
  const lastQueue = useRef<number[]>([]);
  useEffect(() => {
    const ids = queue.map(m => m.id);
    if (focusedId === null || !ids.includes(focusedId)) {
      const oldIndex = focusedId !== null ? lastQueue.current.indexOf(focusedId) : -1;
      const next = oldIndex >= 0
        ? lastQueue.current.slice(oldIndex + 1).find(id => ids.includes(id)) ?? ids[ids.length - 1]
        : ids[0];
      setFocusedId(next ?? null);
    }
    setChecked(prev => {
      const kept = new Set(Array.from(prev).filter(id => ids.includes(id)));
      return kept.size === prev.size ? prev : kept;
    });
    lastQueue.current = ids;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [queueIds]);

  useEffect(() => {
    if (focusedId !== null) cardRefs.current.get(focusedId)?.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
  }, [focusedId]);

  // ── Nouveaux messages : animation, son, titre de l'onglet du navigateur ───
  useEffect(() => {
    const all = [...pending, ...recent].map(m => m.id);
    if (seenIds.current === null) { seenIds.current = new Set(all); return; }
    const fresh = all.filter(id => !seenIds.current!.has(id));
    if (fresh.length === 0) return;
    fresh.forEach(id => seenIds.current!.add(id));
    setFreshIds(new Set(fresh));
    if (sound && pending.some(m => fresh.includes(m.id))) playChime();
  }, [pending, recent, sound]);

  useEffect(() => { onPendingCount?.(pending.length); }, [pending.length, onPendingCount]);

  useEffect(() => {
    const base = document.title.replace(/^\(\d+\) /, '');
    document.title = pending.length > 0 ? `(${pending.length}) ${base}` : base;
  }, [pending.length]);

  useEffect(() => () => { document.title = document.title.replace(/^\(\d+\) /, ''); }, []);

  // ── Toast ─────────────────────────────────────────────────────────────────
  const showToast = useCallback((next: Toast) => {
    setToast(next);
    window.clearTimeout(toastTime.current);
    toastTime.current = window.setTimeout(() => setToast(null), 6000);
  }, []);

  // ── Actions ───────────────────────────────────────────────────────────────
  const decideOne = useCallback(async (id: number, action: ChatDecision, fromFeed = false) => {
    const message = [...pending, ...recent].find(m => m.id === id);
    const ok = await state.decide([id], action);
    if (!ok || !message) return;
    const verb = action === 'approve' ? (fromFeed ? 'publié' : 'publié') : (fromFeed ? 'retiré' : 'refusé');
    showToast({
      text : `Message de ${message.pseudo} ${verb}`,
      undo : () => { state.decide([id], action === 'approve' ? 'reject' : 'approve'); setToast(null); },
    });
  }, [pending, recent, state, showToast]);

  const decideChecked = useCallback(async (action: ChatDecision) => {
    const ids = Array.from(checked);
    if (ids.length === 0) return;
    const ok = await state.decide(ids, action);
    if (!ok) return;
    setChecked(new Set());
    showToast({
      text : `${ids.length} message${ids.length > 1 ? 's' : ''} ${action === 'approve' ? 'publiés' : 'refusés'}`,
      undo : () => { state.decide(ids, action === 'approve' ? 'reject' : 'approve'); setToast(null); },
    });
  }, [checked, state, showToast]);

  const sanction = useCallback(async (id: number, action: 'kick' | 'ban') => {
    setConfirmBanId(null);
    const result = await state.sanction(id, action);
    if (!result) return;
    const extra = result.rejectedPending > 0
      ? ` · ${result.rejectedPending} message${result.rejectedPending > 1 ? 's' : ''} en attente refusé${result.rejectedPending > 1 ? 's' : ''}` : '';
    showToast({ text: action === 'ban' ? `${result.pseudo} est banni de vos chaînes${extra}` : `${result.pseudo} est exclu du salon${extra}` });
  }, [state, showToast]);

  const toggleCheck = (id: number) => setChecked(prev => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });

  const toggleSound = () => setSound(prev => {
    const next = !prev;
    try { window.localStorage.setItem(SOUND_KEY, next ? 'on' : 'off'); } catch { /* préférence du poste */ }
    if (next) playChime();
    return next;
  });

  // ── Clavier ───────────────────────────────────────────────────────────────
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      // Seulement quand l'onglet est à l'écran et qu'on n'écrit pas ailleurs.
      if (!boardRef.current || boardRef.current.offsetParent === null || bansOpen) return;
      const origin = e.target as HTMLElement;
      if (origin.closest('input, textarea, select, [contenteditable="true"]')) return;
      if (e.metaKey || e.ctrlKey || e.altKey) return;

      const ids   = queue.map(m => m.id);
      const key   = e.key.toLowerCase();

      // Le message ciblé : celui qui a le focus s'il est encore dans la file.
      // Juste après une décision, le focus n'a pas encore suivi (la file vient
      // de changer) : on prend alors celui qui a pris sa place.
      let target = focusedId !== null && ids.includes(focusedId) ? focusedId : null;
      if (target === null && ids.length > 0) {
        const oldIndex = focusedId !== null ? lastQueue.current.indexOf(focusedId) : -1;
        target = (oldIndex >= 0 ? lastQueue.current.slice(oldIndex + 1).find(id => ids.includes(id)) : undefined) ?? ids[0];
      }
      const index = target !== null ? ids.indexOf(target) : -1;

      if (confirmBanId !== null) {
        if (key === 'escape') { e.preventDefault(); setConfirmBanId(null); }
        // Entrée sur un bouton : c'est le bouton qui agit, pas le raccourci.
        if (key === 'enter' && origin.tagName === 'BUTTON') return;
        if (key === 'b' || key === 'enter') { e.preventDefault(); sanction(confirmBanId, 'ban'); }
        return;
      }

      if (key === 'j' || key === 'arrowdown') { e.preventDefault(); if (ids.length) setFocusedId(ids[Math.min(ids.length - 1, index + 1)]); return; }
      if (key === 'k' || key === 'arrowup')   { e.preventDefault(); if (ids.length) setFocusedId(ids[Math.max(0, index - 1)]); return; }
      if (key === 'escape') { setChecked(new Set()); return; }

      if (e.shiftKey && (key === 'v' || key === 'r') && checked.size > 0) {
        e.preventDefault();
        decideChecked(key === 'v' ? 'approve' : 'reject');
        return;
      }

      if (target === null) return;
      if (target !== focusedId) setFocusedId(target);
      if (key === 'v') { e.preventDefault(); decideOne(target, 'approve'); }
      if (key === 'r') { e.preventDefault(); decideOne(target, 'reject'); }
      if (key === 'e') { e.preventDefault(); sanction(target, 'kick'); }
      if (key === 'b') { e.preventDefault(); setConfirmBanId(target); }
      if (key === 'x') { e.preventDefault(); toggleCheck(target); }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [queue, focusedId, confirmBanId, checked, bansOpen, decideOne, decideChecked, sanction]);

  // ── Rendu ─────────────────────────────────────────────────────────────────
  const closedFollowed = followed.filter(c => !c.chatOpen);
  const allChecked     = queue.length > 0 && checked.size === queue.length;

  if (channelsLoaded && channels.length === 0 && !error) {
    return (
      <div className={styles.boardWrap} style={{ height: 'auto', minHeight: 0 }}>
        <div className={styles.empty}>
          <strong>Aucune chaîne pour l'instant</strong>
          <p>Le chat se modère chaîne par chaîne. Créez une chaîne dans l'onglet « Chaînes », ouvrez son chat ici, et ses messages arriveront dans cet écran.</p>
        </div>
      </div>
    );
  }

  return (
    <div ref={boardRef} className={styles.boardWrap}>

      {isDemo && (
        <div className={classNames(styles.banner, styles.bannerDemo)}>
          <FlaskConical size={17} /> Démonstration : chaînes et messages fictifs, rien n'est envoyé au serveur. Retirez « ?demoModeration » de l'adresse pour revenir à vos chaînes.
        </div>
      )}

      {error && (
        <div className={styles.banner} role="alert">
          <AlertTriangle size={17} /> <span style={{ flex: 1 }}>{error}</span>
          <button type="button" className={classNames(styles.btn, styles.btnSmall, styles.btnGhost)} onClick={state.clearError}>Masquer</button>
        </div>
      )}

      {closedFollowed.length > 0 && (
        <div className={styles.banner} style={{ color: '#FFE7C7' }}>
          <AlertTriangle size={17} />
          Chat fermé sur {closedFollowed.map(c => c.name).join(', ')} : les spectateurs ne peuvent pas écrire. Ouvrez-le dans « Réglages du chat », à gauche.
        </div>
      )}

      <div className={styles.board}>

      <ChannelRail channels    = {channels}
                   selectedIds = {selectedIds}
                   colorOf     = {colorOf}
                   now         = {now}
                   onToggle    = {state.toggleChannel}
                   onSelectAll = {state.selectChannels}
                   onUpdate    = {state.updateChannel}
                   onOpenBans  = {() => { setBansOpen(true); state.loadBans(); }} />

      <RiskSpectrum channels  = {followed}
                    pending   = {pending}
                    recent    = {recent}
                    colorOf   = {colorOf}
                    now       = {now}
                    focusedId = {focusedId}
                    freshIds  = {freshIds}
                    onFocus   = {setFocusedId} />

      <section className={classNames(styles.panel, styles.queue)} aria-label="Messages à valider">
        <header className={styles.panelHead}>
          <span className={styles.panelTitle}>À valider</span>
          <span className={classNames(styles.count, queue.length === 0 && styles.countZero)}>{queue.length}</span>
          <span className={styles.spacer} />
          <div className={styles.segmented} role="radiogroup" aria-label="Ordre de la file">
            <button type="button" role="radio" aria-checked={sortMode === 'oldest'}
                    className={classNames(styles.segment, sortMode === 'oldest' && styles.segmentOn)}
                    onClick={() => setSortMode('oldest')}>Plus anciens</button>
            <button type="button" role="radio" aria-checked={sortMode === 'risk'}
                    className={classNames(styles.segment, sortMode === 'risk' && styles.segmentOn)}
                    onClick={() => setSortMode('risk')}>Plus douteux</button>
          </div>
          <button type="button" className={classNames(styles.btn, styles.btnSmall, styles.btnGhost)}
                  onClick={toggleSound} aria-pressed={sound}
                  title={sound ? 'Couper le signal sonore des nouveaux messages' : 'Signal sonore à chaque message à valider'}>
            {sound ? <Volume2 size={15} /> : <VolumeX size={15} />}
          </button>
        </header>

        {checked.size > 0 && (
          <div className={styles.bulkBar}>
            <b>{checked.size}</b> sélectionné{checked.size > 1 ? 's' : ''}
            <span className={styles.spacer} />
            <button type="button" className={classNames(styles.btn, styles.btnSmall, styles.btnApprove)} onClick={() => decideChecked('approve')}>
              <Check size={14} /> Tout publier <span className={styles.kbd}>⇧V</span>
            </button>
            <button type="button" className={classNames(styles.btn, styles.btnSmall, styles.btnReject)} onClick={() => decideChecked('reject')}>
              <X size={14} /> Tout refuser <span className={styles.kbd}>⇧R</span>
            </button>
            <button type="button" className={classNames(styles.btn, styles.btnSmall, styles.btnGhost)}
                    onClick={() => setChecked(allChecked ? new Set() : new Set(queue.map(m => m.id)))}>
              {allChecked ? 'Désélectionner' : 'Toute la file'}
            </button>
          </div>
        )}

        <div className={styles.cards}>
          {selectedIds.length === 0 ? (
            <div className={styles.empty}>
              <strong>Choisissez les chats à suivre</strong>
              <p>Cochez une ou plusieurs chaînes à gauche : leurs messages douteux arriveront ici, mêlés dans une seule file.</p>
            </div>
          ) : queue.length === 0 ? (
            <div className={styles.empty}>
              <ShieldCheck size={30} />
              <strong>Rien à valider</strong>
              <p>L'IA publie et refuse seule. Les messages dont elle doute arriveront ici{sound ? ', avec un signal sonore' : ''}.</p>
            </div>
          ) : queue.map(message => {
            const channel = byId.get(message.channelId);
            return (
              <PendingCard key          = {message.id}
                           ref          = {(el: HTMLDivElement | null) => { if (el) cardRefs.current.set(message.id, el); else cardRefs.current.delete(message.id); }}
                           message      = {message}
                           channelName  = {nameOf(message.channelId)}
                           color        = {colorOf(message.channelId)}
                           level        = {CHAT_MODERATION_LEVELS[channel?.level ?? 1] ?? CHAT_MODERATION_LEVELS[1]}
                           now          = {now}
                           focused      = {focusedId === message.id}
                           checked      = {checked.has(message.id)}
                           confirmBan   = {confirmBanId === message.id}
                           onFocus      = {() => setFocusedId(message.id)}
                           onCheck      = {() => toggleCheck(message.id)}
                           onApprove    = {() => decideOne(message.id, 'approve')}
                           onReject     = {() => decideOne(message.id, 'reject')}
                           onKick       = {() => sanction(message.id, 'kick')}
                           onAskBan     = {() => setConfirmBanId(message.id)}
                           onConfirmBan = {() => sanction(message.id, 'ban')}
                           onCancelBan  = {() => setConfirmBanId(null)} />
            );
          })}
        </div>

        <footer className={styles.keys} aria-label="Raccourcis clavier">
          <span><span className={styles.kbd}>J</span> <span className={styles.kbd}>K</span> suivant / précédent</span>
          <span><span className={styles.kbd}>V</span> publier</span>
          <span><span className={styles.kbd}>R</span> refuser</span>
          <span><span className={styles.kbd}>E</span> exclure</span>
          <span><span className={styles.kbd}>B</span> bannir</span>
          <span><span className={styles.kbd}>X</span> sélectionner</span>
        </footer>
      </section>

      <DecisionFeed recent      = {recent}
                    nameOf      = {nameOf}
                    colorOf     = {colorOf}
                    showChannel = {followed.length > 1}
                    onDecide    = {(id, action) => decideOne(id, action, true)} />

      </div>

      {bansOpen && (
        <BansDrawer bans    = {state.bans}
                    onLift  = {async ban => { if (await state.liftBan(ban.id)) showToast({ text: `${ban.pseudo ?? 'Ce spectateur'} peut de nouveau écrire` }); }}
                    onClose = {() => setBansOpen(false)} />
      )}

      {toast && (
        <div className={styles.toast} role="status">
          <span>{toast.text}</span>
          {toast.undo && <button type="button" onClick={toast.undo}><Undo2 size={15} /> Annuler</button>}
        </div>
      )}
    </div>
  );
};

export default ChatModerationBoard;
